import Image from '@/components/Image'
import RichText from '@/components/RichText'
import { EMPTY_KONTENT_RICHTEXT } from '@/constants'
import type { Contentblock } from '@/kontent/content-types/contentblock'
import type { WebUiContentBlocks } from '@/kontent/content-types/web_ui_content_blocks'
import { getLinkFromLinkUI } from '@/utils/getLinkFromLinkUI'
import type { RichtextSectionProps } from '.'
import { ModuleSectionWrapper } from '../ModuleSectionWrapper'
import {
	getLinkedItems,
	useKontentHomeConfig,
} from '../contexts/KontentHomeConfigProvider'
import { GridCol } from '../nsw/grid/GridCol'
import { GridWrapper } from '../nsw/grid/GridWrapper'

export type UiContentBlocksThemeOptions =
	| 'white'
	| 'off_white'
	| 'brand_light'
	| 'brand_dark'

const darkSectionParentSelector = [
	'.nsw-section--brand-dark &',
	'.nsw-section--brand-supplementary &',
	'.nsw-section--black &',
	'.nsw-section--grey-01 &',
	'.nsw-section--grey-02 &',
	'.nsw-section--invert &',
].join(', ')

const getThemeStyles = (
	theme: UiContentBlocksThemeOptions,
): { backgroundColor: string; color: string } => {
	switch (theme) {
		case 'brand_dark':
			return {
				backgroundColor: 'var(--nsw-brand-dark)',
				color: 'var(--nsw-text-light)',
			}
		case 'brand_light':
			return {
				backgroundColor: 'var(--nsw-brand-light)',
				color: 'var(--nsw-text-dark)',
			}
		case 'off_white':
			return {
				backgroundColor: 'var(--nsw-off-white)',
				color: 'var(--nsw-text-dark)',
			}
		case 'white':
		default:
			return {
				backgroundColor: 'var(--nsw-white)',
				color: 'var(--nsw-text-dark)',
			}
	}
}

export default function UiContentBlocks(
	props: RichtextSectionProps<WebUiContentBlocks>,
) {
	const { linkedItem, mappings } = props
	const { pageResponseLinkedItems } = useKontentHomeConfig()

	const displayMode =
		linkedItem.elements.display?.value?.[0]?.codename || 'text'

	const theme = (linkedItem.elements?.theme?.value?.[0]?.codename ||
		'white') as UiContentBlocksThemeOptions
	const themeStyles = getThemeStyles(theme)

	const items = getLinkedItems(
		linkedItem.elements?.items,
		pageResponseLinkedItems,
	)

	const renderImageOrIcon = (item: Contentblock) => {
		if (displayMode === 'image') {
			const imageAsset = item.elements.image?.value?.[0]
			if (!imageAsset) return null
			return (
				<div
					className="nsw-content-block__image relative"
					style={{ height: 200 }}
				>
					<Image
						asset={imageAsset}
						alt={
							imageAsset.description || item.elements.title.value
						}
						layout="fill"
						objectFit="cover"
					/>
				</div>
			)
		}
		if (displayMode === 'icon') {
			const svgAsset = item.elements.svg_icon?.value?.[0]
			if (!svgAsset) return null
			return (
				<div className="nsw-content-block__image">
					<div className="nsw-content-block__icon [&_img]:w-16 [&_img]:h-16">
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							src={svgAsset.url}
							alt={
								svgAsset.description ||
								item.elements.title.value
							}
						/>
					</div>
				</div>
			)
		}
		return null
	}

	return (
		<ModuleSectionWrapper>
			<div
				className="p-6"
				style={themeStyles}
				css={{
					'a, a:hover, a:visited, a:focus': {
						color:
							theme === 'brand_dark'
								? 'var(--nsw-text-light)'
								: 'var(--nsw-brand-dark)',
					},
					'.nsw-content-block': {
						height: '100%',
					},
					...(theme === 'brand_dark' && {
						'.nsw-content-block__icon img': {
							filter: 'brightness(0) invert(1)',
						},
						[darkSectionParentSelector]: {
							'.nsw-content-block': {
								//border: '1px solid var(--nsw-white)',
								//padding: '1rem',
							},
						},
					}),
				}}
			>
				<GridWrapper>
					{items.map((item: Contentblock) => {
						const headline = item.elements.title.value
						const hasContent =
							item.elements.content.value &&
							item.elements.content.value !==
								EMPTY_KONTENT_RICHTEXT

						const navItem = getLinkedItems(
							item.elements.more_info_link,
							pageResponseLinkedItems,
						)?.[0]

						let mainLinkUrl = ''
						let mainLinkText = ''
						if (navItem) {
							const { url } = getLinkFromLinkUI(
								navItem,
								mappings,
								pageResponseLinkedItems,
							)
							if (url) {
								mainLinkUrl = url
								mainLinkText =
									(navItem.elements as any)?.title?.value ||
									item.elements.title.value
							}
						}

						return (
							<GridCol
								key={item.system.id}
								md={4}
								lg={Math.max(12 / items.length, 4)}
								data-kontent-item-id={item.system.id}
							>
								<div className="nsw-content-block">
									{renderImageOrIcon(item)}
									<div className="nsw-content-block__content">
										<h2 className="nsw-content-block__title">
											{headline}
										</h2>
										{hasContent && (
											<RichText
												richTextElement={
													item.elements.content
												}
												linkedItems={
													pageResponseLinkedItems
												}
												mappings={mappings}
											/>
										)}
										{mainLinkUrl && (
											<div className="nsw-content-block__link">
												<a href={mainLinkUrl}>
													{mainLinkText}
												</a>
											</div>
										)}
									</div>
								</div>
							</GridCol>
						)
					})}
				</GridWrapper>
			</div>
		</ModuleSectionWrapper>
	)
}
